import {inject, Injectable} from '@angular/core';
import {defer, Observable, of} from 'rxjs';
import {tap} from 'rxjs/operators';
import {
  DataTableDaoService,
  DataTableRecordsList,
  RecordValue
} from '@q9elements/ui-kit/attachments';
import {SnackBarService} from '@q9elements/ui-kit/common';
import {DtRecordsService} from '../../services/dt-records.service';
import {ActivityCommands} from '../../../commands/activity/activity.commands';
import {DiagramJsonHandlerService} from '../../../diagram-data/diagram-json-handler.service';
import {UndoService} from '../../../commands/undo.service';
import {environment} from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DiagramDataTableDaoService extends DataTableDaoService {
  private undoService = inject(UndoService);
  private diagramJsonService = inject(DiagramJsonHandlerService);
  private activityCommands = inject(ActivityCommands);
  private dtRecordsService = inject(DtRecordsService);
  private snackBarService = inject(SnackBarService);

  constructor() {
    super();
  }

  public override fetchRecords(urlConfig: any): Observable<DataTableRecordsList> {
    const url = this.composeUrl(urlConfig);

    return this.http.get<DataTableRecordsList>(url);
  }

  public override createRecord(data: RecordValue, urlConfig: any): Observable<{recordId: string}> {
    const url = this.composeUrl(urlConfig);

    return this.http.post<{recordId: string}>(url, data).pipe(
      tap(() => {
        this.dtRecordsService.set(true);
        this.snackBarService.success('CORE.DATA_TABLE_ATTACHMENTS.DT_RECORD_ADDED_SUCCESS');
      })
    );
  }

  public override updateRecord(
    recordId: string,
    update: RecordValue,
    urlConfig: any
  ): Observable<unknown> {
    const url = `${this.composeUrl(urlConfig)}/${recordId}`;

    return this.http.put(url, update).pipe(
      tap(() => {
        this.snackBarService.success('CORE.DATA_TABLE_ATTACHMENTS.DT_RECORD_ADDED_SUCCESS');
      })
    );
  }

  public override removeRecord(recordId: string, urlConfig: any): Observable<unknown> {
    const url = `${this.composeUrl(urlConfig)}/${recordId}`;

    return this.http
      .delete(url)
      .pipe(
        tap(() =>
          this.snackBarService.success('CORE.DATA_TABLE_ATTACHMENTS.DT_RECORD_REMOVED_SUCCESS')
        )
      );
  }

  public override updateGroups(groups: string[], urlConfig: any): Observable<unknown> {
    const {itemMxKey, mxKey} = urlConfig;
    const params = {
      groups,
      itemMxKey,
      mxKey
    };

    return defer(() => {
      const command = this.activityCommands.changeAttachment(params);

      this.undoService.executeCommand(command, true);

      return of(null);
    });
  }

  public override updateAddRecordsPermission(
    viewersCanAddRecords: boolean,
    urlConfig: any
  ): Observable<unknown> {
    const {itemMxKey, mxKey} = urlConfig;
    const update = {
      viewersCanAddRecords,
      mxKey,
      itemMxKey
    };

    return defer(() => {
      this.diagramJsonService.update({
        changeAttachment: update
      });

      const command = this.activityCommands.changeAttachment(update);

      this.undoService.executeCommand(command);

      return of(null);
    });
  }

  private composeUrl(urlConfig: any): string {
    const {diagramId, diagramState, mxKey, itemMxKey} = urlConfig;

    return [
      environment.API_ENDPOINT,
      '/diagram/',
      diagramId,
      '/state/',
      diagramState,
      '/activity/',
      itemMxKey,
      '/attachment/',
      mxKey,
      '/record'
    ].join('');
  }
}
